import { reactive, effect, isReactive, stop, DebuggerEvent, toRaw } from '@vue/reactivity';
import { isObject, isArray, isFunction, hasChanged, NOOP, isMap, isSet } from '@vue/shared';

const INITIAL_WATCHER_VALUE = {};

interface LooseObject {
  [key: string]: any
}

const enum WatchFlags {
  REFRESH_ID = '___rid',
}

interface WatchOptions {
  immediate?: boolean,
}

export interface ChangeDetail {
  k: (string | number)[]
  v: unknown
}

/**
 * 对象遍历
 * @param value
 * @param seen
 */
const traverse = (value: unknown, seen: Set<unknown> = new Set()) => {
  if (!isObject(value) || seen.has(value)) {
    return value;
  }
  seen.add(value);
  if (isArray(value) || isSet(value) || isMap(value)) {
    value.forEach((v: unknown) => {
      traverse(v, seen);
    });
  } else {
    Object.keys(value).forEach((key) => {
      traverse(value[key], seen);
    });
  }
  return value;
};

export class Watch {
  // 监听内容所在的源对象
  public original: LooseObject;

  // 监听的内容
  public source: any;

  // 是否回调监听变化的回调函数
  private isRefresh = false;

  // 累加值，用于控制更新源对象
  private rid = 0;

  // 回调任务存储 list
  private jobs: Function[] = [];

  // 是否在缓冲状态
  private isFlushing = false;

  public constructor(original: object, source?: unknown) {
    this.original = original;
    // 挂载用于强制刷新的字段
    this.original[WatchFlags.REFRESH_ID] = this.rid;
    this.source = source || reactive(original);
  }

  /**
   * 设置监听对象和变更回调
   * @param cb 内容更新回调
   * @param {
   *   immediate: 首次监听是否立即触发 cb 回调
   * }
   */
  public doWatch(
    cb: Function,
    { immediate }: WatchOptions = {},
  ) {
    const { source } = this;

    // getter
    let getter: () => any;
    let deep = false;
    if (isReactive(source)) {
      getter = () => source;
      deep = true;
    } else if (isFunction(source)) {
      getter = () => source();
    } else {
      getter = NOOP;
    }
    if (cb && deep) {
      const baseGetter = getter;
      getter = () => traverse(baseGetter());
    }

    let cleanup: () => void;
    const onInvalidate = (fn: () => void) => {
      cleanup = fn;
      runner.options.onStop = fn;
    };

    // 监听回调任务设置
    const triggers: DebuggerEvent[] = [];
    let oldValue = isArray(source) ? [] : INITIAL_WATCHER_VALUE;
    const job = () => {
      if (!runner.active) {
        return;
      }
      const newValue = runner();
      if (deep || hasChanged(newValue, oldValue)) {
        cleanup?.();
        // 如果直接源数据变更，则不触发更新回调
        if (!this.isRefresh) {
          cb(...[
            newValue,
            oldValue === INITIAL_WATCHER_VALUE ? undefined : oldValue,
            this.mergeTrigger(triggers),
            onInvalidate,
          ]);
        }
        this.isRefresh = false;
        triggers.length = 0;
        oldValue = newValue;
      }
    };

    const runner = effect(getter, {
      lazy: true,
      scheduler: () => {
        this.queueFlushCb(job);
      },
      onTrigger: (trigger) => {
        triggers.push(trigger);
      },
    });

    if (immediate) {
      job();
    } else {
      oldValue = runner();
    }

    return () => {
      stop(runner);
    };
  }

  /**
   * 刷新回调，用于监听的源对象发生变更的时候
   */
  public refresh() {
    this.isRefresh = true;
    this.rid += 1;
    this.source[WatchFlags.REFRESH_ID] = this.rid;
  }

  /**
   * 任务调度器
   * @param job
   */
  private queueFlushCb(job: Function) {
    if (!this.jobs.includes(job)) this.jobs.push(job);
    if (!this.isFlushing) {
      this.isFlushing = true;
      Promise.resolve().then(() => {
        this.isFlushing = false;
        let fn;
        while (fn = this.jobs.shift()) {
          fn();
        }
      });
    }
  };

  /**
   * 收集触发变更的操作集合
   * @param triggers
   * @returns changes 变更操作集合
   */
  private mergeTrigger(triggers: DebuggerEvent[]) {
    const changes: ChangeDetail[] = [];
    /**
     * 递归遍历
     * @param original 源对象
     * @param triggers 变更
     * @param keys 源对象查找路径
     */
    const traverseOriginal = (original: unknown, triggers: DebuggerEvent[], keys: (string | number)[] = []) => {
      // 设置下一个遍历的路径
      const pushKeys = (val: (string | number)) => {
        const newKeys = keys.slice();
        newKeys.push(val);
        return newKeys;
      };

      // 收集当前源对象的变更内容
      const matchTriggers: DebuggerEvent[] = [];
      const len = triggers.length;
      for (let index = len - 1; index >= 0; index--) {
        const t = triggers[index];
        if (toRaw(original) === toRaw(t.target)) {
          matchTriggers.unshift(t);
          triggers.splice(index, 1);
        }
      }

      // 遍历当前源对象的变更，转换为 { k: 路径，v: 变更后的值 }
      matchTriggers.forEach((t) => {
        if (isArray(t.target) || isSet(t.target)) {
          changes.push({
            k: keys,
            v: t.target,
          });
        } else {
          changes.push({
            k: t.key === undefined ? keys : pushKeys(t.key),
            v: t.newValue,
          });
        }
      });
      // 不需要继续遍历则返回
      if (triggers.length === 0 || !isObject(original)) return;

      // 对于复杂类型继续向下遍历收集
      if (isArray(original) || isSet(original)) {
        original.forEach((v: unknown, i: number) => {
          traverseOriginal(v, triggers, pushKeys(i));
        });
      } else {
        Object.keys(original).forEach((key) => {
          traverseOriginal(original[key], triggers, pushKeys(key));
        });
      }
    };

    traverseOriginal(this.original, triggers);

    return changes;
  }
}
